import React, { useState, useEffect, useRef, useCallback } from 'react';
import { onGameEvent, emitGameEvent } from '../../core/GameEvents';
import { networkManager } from '../../systems/NetworkManager';

const PALETTE = {
  DARK: '#0f1b26',
  LIGHT: '#f5e8d1',
  TEAL: '#20a5a6',
  ORANGE: '#dd5639',
  DIM: '#4a5a6a',
  PANEL: '#1a2a38',
};

type ChatChannel = 'global' | 'local' | 'party' | 'corp';

interface ChatMessage {
  id: string;
  playerId: string;
  playerName: string;
  channel: ChatChannel;
  message: string;
  timestamp: number;
  isSystem?: boolean;
}

interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const MAX_MESSAGES = 100;

const panelStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 10,
  left: 10,
  width: 420,
  height: 280,
  backgroundColor: 'rgba(15, 27, 38, 0.45)',
  border: `1px solid ${PALETTE.DIM}`,
  borderRadius: '4px',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '11px',
  pointerEvents: 'auto',
};

const minimizedStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 10,
  left: 10,
  padding: '8px 16px',
  backgroundColor: 'rgba(15, 27, 38, 0.45)',
  border: `1px solid ${PALETTE.DIM}`,
  borderRadius: '4px',
  cursor: 'pointer',
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '11px',
  color: PALETTE.TEAL,
  pointerEvents: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '6px 10px',
  borderBottom: `1px solid ${PALETTE.DIM}`,
  backgroundColor: PALETTE.PANEL,
};

const tabsStyle: React.CSSProperties = {
  display: 'flex',
  gap: 0,
};

const tabStyle: React.CSSProperties = {
  padding: '4px 12px',
  cursor: 'pointer',
  color: PALETTE.DIM,
  borderBottom: '2px solid transparent',
  transition: 'color 0.2s',
};

const activeTabStyle: React.CSSProperties = {
  ...tabStyle,
  color: PALETTE.TEAL,
  borderBottomColor: PALETTE.TEAL,
};

const messagesStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const messageStyle: React.CSSProperties = {
  wordBreak: 'break-word',
  lineHeight: 1.3,
};

const inputContainerStyle: React.CSSProperties = {
  padding: '8px',
  borderTop: `1px solid ${PALETTE.DIM}`,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  backgroundColor: PALETTE.DARK,
  border: `1px solid ${PALETTE.DIM}`,
  borderRadius: '4px',
  color: PALETTE.LIGHT,
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '11px',
  outline: 'none',
  boxSizing: 'border-box',
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: PALETTE.DIM,
  cursor: 'pointer',
  fontSize: '11px',
  padding: '2px 6px',
};

const unreadBadgeStyle: React.CSSProperties = {
  backgroundColor: PALETTE.ORANGE,
  color: PALETTE.DARK,
  borderRadius: '10px',
  padding: '2px 6px',
  fontSize: '10px',
  fontWeight: 600,
};

export function ChatPanel({ isOpen, onClose }: ChatPanelProps) {
  const [messages, setMessages] = useState<Map<ChatChannel, ChatMessage[]>>(
    new Map([
      ['global', []],
      ['local', []],
      ['party', []],
      ['corp', []],
    ])
  );
  const [activeChannel, setActiveChannel] = useState<ChatChannel>('local');
  const [inputValue, setInputValue] = useState('');
  const [unreadCount, setUnreadCount] = useState<Map<ChatChannel, number>>(
    new Map([
      ['global', 0],
      ['local', 0],
      ['party', 0],
      ['corp', 0],
    ])
  );
  // The corp tab only exists for members (their own ship carries a corpTag).
  const [isCorpMember, setIsCorpMember] = useState(false);
  useEffect(() => {
    const check = () => {
      const room: any = (networkManager as any).room;
      const me = room?.state?.players?.get?.(room.sessionId);
      setIsCorpMember(!!me?.corpTag);
    };
    check();
    const t = setInterval(check, 3000);
    return () => clearInterval(t);
  }, []);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, activeChannel, scrollToBottom]);

  // Subscribe to chat messages
  useEffect(() => {
    // Set up NetworkManager callback
    networkManager.onChatMessage = (data) => {
      setMessages((prev) => {
        const newMap = new Map(prev);
        const channelMessages = [...(newMap.get(data.channel) || [])];
        channelMessages.push(data);

        // Trim old messages
        while (channelMessages.length > MAX_MESSAGES) {
          channelMessages.shift();
        }

        newMap.set(data.channel, channelMessages);
        return newMap;
      });

      // Update unread if not active channel or panel closed
      if (!isOpen || data.channel !== activeChannel) {
        setUnreadCount((prev) => {
          const newMap = new Map(prev);
          newMap.set(data.channel, (newMap.get(data.channel) || 0) + 1);
          return newMap;
        });
      }
    };

    return () => {
      networkManager.onChatMessage = null;
    };
  }, [isOpen, activeChannel]);

  // Clear unread when switching channels or opening
  useEffect(() => {
    if (isOpen) {
      setUnreadCount((prev) => {
        const newMap = new Map(prev);
        newMap.set(activeChannel, 0);
        return newMap;
      });
    }
  }, [isOpen, activeChannel]);


  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    networkManager.sendChatMessage(activeChannel, trimmed);
    setInputValue('');
  }, [inputValue, activeChannel]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSend();
    } else if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      const channels: ChatChannel[] = isCorpMember
        ? ['global', 'local', 'party', 'corp']
        : ['global', 'local', 'party'];
      const idx = channels.indexOf(activeChannel);
      setActiveChannel(channels[(idx + 1) % channels.length]);
    }
    e.stopPropagation();
  }, [handleSend, onClose, activeChannel, isCorpMember]);

  const totalUnread = Array.from(unreadCount.values()).reduce((a, b) => a + b, 0);

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const channelMessages = messages.get(activeChannel) || [];

  if (!isOpen) {
    return (
      <div style={minimizedStyle} onClick={onClose}>
        <span>CHAT</span>
        {totalUnread > 0 && (
          <span style={unreadBadgeStyle}>
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </div>
    );
  }

  return (
    <div style={panelStyle}>
      <div style={headerStyle}>
        <div style={tabsStyle}>
          {((isCorpMember ? ['global', 'local', 'party', 'corp'] : ['global', 'local', 'party']) as ChatChannel[]).map((channel) => {
            const channelUnread = unreadCount.get(channel) || 0;
            return (
              <div
                key={channel}
                style={channel === activeChannel ? activeTabStyle : tabStyle}
                onClick={() => setActiveChannel(channel)}
              >
                {channel.toUpperCase()}
                {channelUnread > 0 && channel !== activeChannel && (
                  <span style={{ ...unreadBadgeStyle, marginLeft: 4 }}>
                    {channelUnread}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <button style={closeButtonStyle} onClick={onClose}>
          ×
        </button>
      </div>

      <div style={messagesStyle}>
        {channelMessages.length === 0 ? (
          <div style={{ color: PALETTE.DIM, textAlign: 'center', marginTop: 20 }}>
            No messages yet
          </div>
        ) : (
          channelMessages.map((msg) => (
            <div key={msg.id} style={messageStyle}>
              <span style={{ color: PALETTE.DIM, marginRight: 6 }}>
                [{formatTime(msg.timestamp)}]
              </span>
              {msg.isSystem ? (
                <span style={{ color: PALETTE.DIM, fontStyle: 'italic' }}>
                  {msg.message}
                </span>
              ) : (
                <>
                  <span style={{ color: PALETTE.TEAL, fontWeight: 600 }}>
                    {msg.playerName}:
                  </span>{' '}
                  <span style={{ color: PALETTE.LIGHT }}>{msg.message}</span>
                </>
              )}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div style={inputContainerStyle}>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Message #${activeChannel}...`}
          maxLength={200}
          style={inputStyle}
          onFocus={(e) => (e.currentTarget.style.borderColor = PALETTE.TEAL)}
          onBlur={(e) => (e.currentTarget.style.borderColor = PALETTE.DIM)}
        />
      </div>
    </div>
  );
}

export default ChatPanel;
